import clsx from "clsx";
import { Plus, X, Check, Edit, RefreshCw, Flag, ChevronsUpDown } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { CopyIcon } from "@/src/components/copyable-content";
import { Button } from "@/src/components/shadcn/ui/button";
import { Card } from "@/src/components/shadcn/ui/card";
import { Checkbox } from "@/src/components/shadcn/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/src/components/shadcn/ui/command";
import { DatePicker } from "@/src/components/shadcn/ui/date-picker";
import { Input } from "@/src/components/shadcn/ui/input";
import { Label } from "@/src/components/shadcn/ui/label";
import { MultiSelect } from "@/src/components/shadcn/ui/multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/shadcn/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/src/components/shadcn/ui/select";
import { Textarea } from "@/src/components/shadcn/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/src/components/shadcn/ui/tooltip";
import { useExtractionAnnotationPopover } from "@/src/context/extraction-annotation-popover-context";
import { cn } from "@/src/lib/shadcn-utils";
import {
  calculateFormattedCursorPosition,
  convertDateStringToUSFormat,
  formatPhoneNumberAsTyped,
  formatSSNAsTyped,
  formatUSPhoneNumber,
} from "@/src/lib/utils/date-and-phone-utils";
import { isEql, isEqlArr } from "@/src/lib/utils/object-utils";

const TextareaWrapper: React.FC<{
  value: string | undefined | null;
  secondaryValue?: string | undefined | null;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  label: string | null;
  isRequired?: boolean;
  size?: "sm" | "md" | "lg";
  onSave?: () => void;
  doesConflict?: boolean;
  onFlagForInaccuracyUpdate?: (key: string, value: any) => void;
  fieldKey?: string;
  tabIndex?: number;
}> = ({
  value,
  secondaryValue,
  onChange,
  label,
  size = "md",
  onSave,
  doesConflict,
  onFlagForInaccuracyUpdate,
  fieldKey,
  tabIndex,
}) => {
  const [showActionButtons, setShowActionButtons] = useState(false);
  const [originalValue, setOriginalValue] = useState<string | undefined | null>(value);

  // Track original value for comparison
  useEffect(() => {
    if (!showActionButtons) {
      setOriginalValue(value);
    }
  }, [value, showActionButtons]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault();

    e.stopPropagation();

    setShowActionButtons(true);

    onChange(e);
  };

  const handleSave = () => {
    setShowActionButtons(false);

    // Update original value to current value so the widget disappears
    setOriginalValue(value);

    // Call onFlagForInaccuracyUpdate if provided and we have a fieldKey
    onFlagForInaccuracyUpdate?.(fieldKey || "", value);

    onSave?.();
  };

  return (
    <div>
      {label && (
        <div className="flex items-center justify-between">
          <Label className="text-muted-foreground">{label}</Label>
          {showActionButtons && value !== originalValue && (
            <TooltipProvider>
              <Tooltip delayDuration={2000}>
                <TooltipTrigger asChild>
                  <Flag
                    className="h-3 w-3 text-orange-500 hover:text-orange-700 cursor-pointer ml-1"
                    onClick={(e) => {
                      e.stopPropagation();

                      handleSave();
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <span>Flag for AI extraction inaccuracy</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
      <Textarea
        value={value || ""}
        onChange={handleChange}
        className={`mt-2 border-muted/80 ${size === "sm" ? "h-20" : size === "lg" ? "h-40" : "h-32"}`}
        onBlur={onSave}
        tabIndex={tabIndex}
      />

      {doesConflict && secondaryValue && (
        <div className="flex items-center mt-1 text-xs text-muted-foreground justify-between">
          <span className="ml-1 pl-1">{secondaryValue}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs px-2 py-0 hover:bg-muted rounded-full flex items-center justify-center"
            onClick={(e) => {
              e.preventDefault();

              onChange({ target: { value: secondaryValue } } as React.ChangeEvent<HTMLTextAreaElement>);

              if (onSave) onSave();
            }}
          >
            Switch
            <RefreshCw className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

const formatValue = (value: string | undefined | null, type: string) => {
  if (type === "tel") {
    return formatUSPhoneNumber(value || null) || value || "";
  }

  return value || "";
};

const getBorderColor = (
  doesConflict: boolean | undefined,
  isRequired: boolean | undefined,
  val: string | null | undefined
) => {
  if (isRequired && !val) {
    return "border-destructive dark:border-destructive border-dashed";
  }

  return doesConflict ? "border-orange-500 dark:border-orange-500" : "";
};

const getInputClasses = (
  primaryValue: string | undefined | null,
  secondaryValue: string | undefined | null,
  readOnly?: boolean
) => {
  if (readOnly) {
    return "bg-muted/30 cursor-default focus-visible:ring-0 focus-visible:outline-none";
  }

  if (secondaryValue && primaryValue === secondaryValue) {
    return "bg-muted/30 ";
  }

  return "bg-input";
};

const InputWrapper = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  {
    value: string | undefined | null;
    secondaryValue?: string | undefined | null;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    label: string | null;
    placeholder?: string;
    isRequired?: boolean;
    type?: string;
    onSave?: () => void;
    onFocus?: () => void;
    doesConflict?: boolean;
    readOnly?: boolean;
    onFlagForInaccuracyUpdate?: (key: string, value: any) => void;
    fieldKey?: string;
    error?: string | null;
    tabIndex?: number;
    className?: string;
    multiline?: boolean;
  }
>(
  (
    {
      value,
      secondaryValue,
      onChange,
      label,
      placeholder,
      type = "text",
      onSave,
      onFocus,
      doesConflict,
      readOnly,
      isRequired,
      onFlagForInaccuracyUpdate,
      fieldKey,
      error,
      tabIndex,
      className,
      multiline,
    },
    ref
  ) => {
    const [showActionButtons, setShowActionButtons] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Capture initial value when component first mounts.
    const [initialValue] = useState(value);

    // Check if current value differs from initial value
    const hasChangedFromInitial = !isEql(value || "", initialValue || "");

    const formattedValue = formatValue(value, type);
    const formattedSecondaryValue = formatValue(secondaryValue, type);

    // Set showActionButtons to true when value changes from initial
    useEffect(() => {
      if (hasChangedFromInitial) {
        setShowActionButtons(true);
      }
    }, [hasChangedFromInitial]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();

      e.preventDefault();

      setShowActionButtons(true);

      // Auto-format phone numbers and SSN as user types
      if (type === "tel" || type === "ssn") {
        const oldValue = e.target.value;
        const formattedValue = type === "tel" ? formatPhoneNumberAsTyped(oldValue) : formatSSNAsTyped(oldValue);
        const cursorPosition = calculateFormattedCursorPosition(oldValue, formattedValue, e.target.selectionStart || 0);

        onChange({ ...e, target: { ...e.target, value: formattedValue } });

        // Set cursor position after state update
        setTimeout(() => {
          inputRef.current?.setSelectionRange(cursorPosition, cursorPosition);
        }, 0);
      } else {
        onChange(e);
      }
    };

    const handleSave = () => {
      setShowActionButtons(false);

      // Call onFlagForInaccuracyUpdate if provided and we have a fieldKey
      onFlagForInaccuracyUpdate?.(fieldKey || "", value);

      onSave?.();
    };

    const inputClassName = `min-w-0 flex-1 focus:border-none  ${getBorderColor(doesConflict, isRequired, value)} ${getInputClasses(value, secondaryValue, readOnly)}`;
    const sharedFieldProps = {
      autoComplete: "off",
      value: formattedValue,
      onBlur: onSave,
      readOnly,
      placeholder,
      tabIndex,
      onFocus,
    };

    // Show the "Switch" helper when we have an alternate value that differs from the current one,
    // and either (a) the field is marked as conflicting or (b) the primary value is empty/falsy.
    const hasConflictOrEmptyPrimary = Boolean(doesConflict) || !formattedValue;
    const showSecondaryOption =
      !!secondaryValue && formattedSecondaryValue !== formattedValue && hasConflictOrEmptyPrimary;

    return (
      <div className={clsx("px-1", className)}>
        {label && (
          <div className="flex items-center justify-between">
            <Label className="text-muted-foreground">{label}</Label>
            {onFlagForInaccuracyUpdate && showActionButtons && hasChangedFromInitial && (
              <TooltipProvider>
                <Tooltip delayDuration={2000}>
                  <TooltipTrigger asChild>
                    <Flag
                      className="h-3 w-3 text-orange-500 hover:text-orange-700"
                      onClick={(e) => {
                        e.stopPropagation();

                        handleSave();
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>Flag for AI extraction inaccuracy</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
        <div className={clsx("group mt-2 flex gap-1", multiline ? "items-start" : "items-center")}>
          {multiline ? (
            <Textarea
              ref={(node) => {
                if (typeof ref === "function") ref(node);
                else if (ref) ref.current = node;
              }}
              rows={1}
              className={`min-h-9 max-h-60 resize-none border-zinc-200 py-1.5 [field-sizing:content] dark:border-zinc-800 ${inputClassName}`}
              onChange={(e) => {
                e.stopPropagation();

                setShowActionButtons(true);

                onChange(e as unknown as React.ChangeEvent<HTMLInputElement>);
              }}
              {...sharedFieldProps}
            />
          ) : (
            <Input
              ref={(node) => {
                inputRef.current = node;

                if (typeof ref === "function") ref(node);
                else if (ref) ref.current = node;
              }}
              className={inputClassName}
              onChange={handleChange}
              type={type}
              onKeyDown={(e) => {
                if (type === "number" && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
                  e.preventDefault();
                }
              }}
              {...sharedFieldProps}
            />
          )}
          <CopyIcon text={type === "date" ? convertDateStringToUSFormat(formattedValue) : formattedValue} />
        </div>
        {error && <span className="text-xs text-destructive mt-1 ml-1">{error}</span>}

        {showSecondaryOption && (
          <div className="flex items-center mt-1 text-xs text-muted-foreground justify-between">
            <span className="ml-1 pl-2 min-w-0 whitespace-pre-wrap break-words">{formattedSecondaryValue}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs px-2 py-0 hover:bg-muted rounded-full flex items-center justify-center"
              onClick={(e) => {
                e.preventDefault();

                onChange({ target: { value: formattedSecondaryValue } } as React.ChangeEvent<HTMLInputElement>);

                if (onSave) onSave();
              }}
            >
              Switch
              <RefreshCw className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    );
  }
);

InputWrapper.displayName = "InputWrapper";

const CheckBoxWrapper: React.FC<{
  checked: boolean;
  secondaryChecked?: boolean;
  onChange: (value: boolean) => void;
  label: string | null;
  doesConflict?: boolean;
  onFocus?: () => void;
  onFlagForInaccuracyUpdate?: (key: string, value: any) => void;
  fieldKey?: string;
  tabIndex?: number;
  disabled?: boolean;
}> = ({
  checked,
  secondaryChecked,
  onChange,
  label,
  doesConflict,
  onFocus,
  onFlagForInaccuracyUpdate,
  fieldKey,
  tabIndex,
  disabled = false,
}) => {
  const [showActionButtons, setShowActionButtons] = useState(false);
  const [initialChecked] = useState(checked);

  // Check if current value differs from initial value
  const hasChangedFromInitial = checked !== initialChecked;

  // Set showActionButtons to true when value changes from initial
  useEffect(() => {
    if (hasChangedFromInitial) {
      setShowActionButtons(true);
    }
  }, [hasChangedFromInitial]);

  const handleChange = (checked: boolean) => {
    setShowActionButtons(true);

    onChange(checked);
  };

  const handleSave = () => {
    setShowActionButtons(false);

    onFlagForInaccuracyUpdate?.(fieldKey || "", checked);
  };

  return (
    <div className="px-1">
      <div
        onClick={disabled ? undefined : () => handleChange(!checked)}
        onFocus={onFocus}
        tabIndex={disabled ? -1 : (tabIndex ?? 0)}
        className={`w-full flex items-center justify-between py-2  ${
          disabled ? "cursor-default opacity-50" : "hover:bg-muted/80"
        } ${doesConflict ? "border border-warn-foreground rounded-md" : ""}`}
      >
        {label && <Label className="text-muted-foreground">{label}</Label>}
        <div className="flex items-center">
          {onFlagForInaccuracyUpdate && showActionButtons && hasChangedFromInitial && (
            <TooltipProvider>
              <Tooltip delayDuration={2000}>
                <TooltipTrigger asChild>
                  <Flag
                    className="h-3 w-3 text-orange-500 hover:text-orange-700 mr-2"
                    onClick={(e) => {
                      e.stopPropagation();

                      handleSave();
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <span>Flag for AI extraction inaccuracy</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <Checkbox
            className=""
            checked={checked}
            onCheckedChange={disabled ? undefined : handleChange}
            disabled={disabled}
          />
        </div>
      </div>
      {doesConflict && secondaryChecked !== undefined && (
        <div className="flex items-center mt-1 text-xs text-muted-foreground justify-between">
          <span className="ml-1 pl-1">{secondaryChecked ? "Checked" : "Unchecked"}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs px-2 py-0 hover:bg-muted rounded-full flex items-center justify-center"
            onClick={(e) => {
              e.preventDefault();

              onChange(secondaryChecked);
            }}
          >
            Switch
            <RefreshCw className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

const SelectWrapper: React.FC<{
  value: string | undefined | null;
  secondaryValue?: string | undefined | null;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  label: string | null;
  placeholder?: string | null;
  isRequired?: boolean;
  options: { value: string; label: string; icon?: React.ReactNode }[];
  doesConflict?: boolean;
  onFlagForInaccuracyUpdate?: (key: string, value: any) => void;
  fieldKey?: string;
  error?: string;
  disabled?: boolean;
  tabIndex?: number;
  allowDeselect?: boolean;
}> = ({
  value,
  secondaryValue,
  onChange,
  onFocus,
  onBlur,
  label,
  placeholder,
  options,
  doesConflict,
  onFlagForInaccuracyUpdate,
  fieldKey,
  error,
  disabled,
  tabIndex,
  allowDeselect,
}) => {
  const placeholderVal = placeholder || "Select an option";
  const selectedOption = options.find((option) => option.value === value);
  const openExtractionAnnotation = useExtractionAnnotationPopover();
  const isSelectOpenRef = useRef(false);
  const selectionChangedRef = useRef(false);
  const [showActionButtons, setShowActionButtons] = useState(false);

  // Capture initial value when component first mounts.
  const [initialValue] = useState(value);

  // Check if current value differs from initial value
  const hasChangedFromInitial = !isEql(value || "", initialValue || "");

  // Set showActionButtons to true when value changes from initial
  useEffect(() => {
    if (hasChangedFromInitial) {
      setShowActionButtons(true);
    }
  }, [hasChangedFromInitial]);

  const handleValueChange = (newValue: string) => {
    setShowActionButtons(true);
    selectionChangedRef.current = true;

    onChange(newValue);
  };

  const handleSave = () => {
    setShowActionButtons(false);

    // Call onFlagForInaccuracyUpdate if provided and we have a fieldKey
    onFlagForInaccuracyUpdate?.(fieldKey || "", value);

    onBlur?.();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleValueChange("");
    selectionChangedRef.current = false;
    openExtractionAnnotation?.();
  };

  const showClearButton = allowDeselect && !!value && !disabled;

  return (
    <div>
      <div className="mb-2">
        {label && (
          <div className="flex items-center justify-between">
            <Label className="text-muted-foreground">{label}</Label>
            {onFlagForInaccuracyUpdate && showActionButtons && hasChangedFromInitial && (
              <TooltipProvider>
                <Tooltip delayDuration={2000}>
                  <TooltipTrigger asChild>
                    <Flag
                      className="h-3 w-3 text-orange-500 hover:text-orange-700"
                      onClick={(e) => {
                        e.stopPropagation();

                        handleSave();
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>Flag for AI extraction inaccuracy</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </div>
      <div className="relative">
        <Select
          value={value || ""}
          onValueChange={handleValueChange}
          onOpenChange={(open) => {
            isSelectOpenRef.current = open;

            if (!open && selectionChangedRef.current) {
              selectionChangedRef.current = false;
              openExtractionAnnotation?.();
            }

            // Fix a race condition between input blur and onOpenChange
            setTimeout(() => {
              if (open) {
                onFocus?.();
              }
            }, 0);
          }}
        >
          <SelectTrigger
            onFocus={onFocus}
            onBlur={() => {
              // Prevents the blur event from being called post-selection.
              if (!isSelectOpenRef.current) {
                onBlur?.();
              }
            }}
            disabled={disabled}
            tabIndex={tabIndex}
            className={`bg-input disabled:opacity-100 disabled:bg-muted/30 disabled:cursor-default ${
              showClearButton ? "pr-8" : ""
            }`}
          >
            {selectedOption?.icon ? (
              <div className="flex items-center space-x-2">
                {selectedOption.icon}
                <span>{selectedOption.label}</span>
              </div>
            ) : (
              <SelectValue placeholder={placeholderVal} />
            )}
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {/* <SelectLabel>{label}</SelectLabel> */}
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex items-center">
                    {option.icon}
                    {option.label}
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        {showClearButton && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent text-muted-foreground hover:text-foreground z-10"
            onClick={handleClear}
            aria-label="Clear selection"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

      {doesConflict && secondaryValue && (
        <div className="flex items-center mt-1 text-xs text-muted-foreground justify-between">
          <span className="ml-1 pl-1">{options.find((o) => o.value === secondaryValue)?.label || secondaryValue}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs px-2 py-0 hover:bg-muted rounded-full flex items-center justify-center"
            onClick={(e) => {
              e.preventDefault();

              onChange(secondaryValue);
            }}
          >
            Switch
            <RefreshCw className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

const SearchableSelectWrapper: React.FC<{
  value: string | undefined | null;
  secondaryValue?: string | undefined | null;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  label: string | null;
  placeholder?: string | null;
  isRequired?: boolean;
  options: { value: string; label: string; icon?: React.ReactNode }[];
  doesConflict?: boolean;
  onFlagForInaccuracyUpdate?: (key: string, value: any) => void;
  fieldKey?: string;
  error?: string;
  disabled?: boolean;
  tabIndex?: number;
  allowDeselect?: boolean;
}> = ({
  value,
  secondaryValue,
  onChange,
  onFocus,
  onBlur,
  label,
  placeholder,
  options,
  doesConflict,
  onFlagForInaccuracyUpdate,
  fieldKey,
  error,
  disabled,
  tabIndex,
  allowDeselect,
}) => {
  const placeholderVal = placeholder || "Select an option";
  const selectedOption = options.find((option) => option.value === value);
  const openExtractionAnnotation = useExtractionAnnotationPopover();
  const [open, setOpen] = useState(false);
  const [showActionButtons, setShowActionButtons] = useState(false);

  const [initialValue] = useState(value);
  const hasChangedFromInitial = !isEql(value || "", initialValue || "");

  useEffect(() => {
    if (hasChangedFromInitial) {
      setShowActionButtons(true);
    }
  }, [hasChangedFromInitial]);

  const handleValueChange = (newValue: string) => {
    setShowActionButtons(true);
    onChange(newValue);
  };

  const handleSelect = (newValue: string) => {
    handleValueChange(newValue);
    setOpen(false);
    openExtractionAnnotation?.();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleValueChange("");
    openExtractionAnnotation?.();
  };

  const showClearButton = allowDeselect && !!value && !disabled;

  return (
    <div>
      <div className="mb-2">
        {label && (
          <div className="flex items-center justify-between">
            <Label className="text-muted-foreground">{label}</Label>
            {onFlagForInaccuracyUpdate && showActionButtons && hasChangedFromInitial && (
              <TooltipProvider>
                <Tooltip delayDuration={2000}>
                  <TooltipTrigger asChild>
                    <Flag
                      className="h-3 w-3 text-orange-500 hover:text-orange-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowActionButtons(false);
                        onFlagForInaccuracyUpdate?.(fieldKey || "", value);
                        onBlur?.();
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>Flag for AI extraction inaccuracy</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </div>
      <div className="relative">
        <Popover
          open={open}
          onOpenChange={(nextOpen) => {
            setOpen(nextOpen);

            if (nextOpen) {
              onFocus?.();
            } else {
              onBlur?.();
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              tabIndex={tabIndex}
              onFocus={onFocus}
              onBlur={() => {
                if (!open) {
                  onBlur?.();
                }
              }}
              className={`w-full justify-between bg-input font-normal disabled:opacity-100 disabled:bg-muted/30 disabled:cursor-default ${
                showClearButton ? "pr-8" : ""
              }`}
            >
              <span className={cn("truncate", !selectedOption && "text-muted-foreground")}>
                {selectedOption ? (
                  <span className="flex items-center">
                    {selectedOption.icon}
                    {selectedOption.label}
                  </span>
                ) : (
                  placeholderVal
                )}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search..." />
              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={`${option.value} ${option.label}`}
                      onSelect={() => handleSelect(option.value)}
                    >
                      <Check
                        className={cn("mr-2 h-4 w-4 shrink-0", value === option.value ? "opacity-100" : "opacity-0")}
                      />
                      {option.icon}
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {showClearButton && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-8 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent text-muted-foreground hover:text-foreground z-10"
            onClick={handleClear}
            aria-label="Clear selection"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

      {doesConflict && secondaryValue && (
        <div className="flex items-center mt-1 text-xs text-muted-foreground justify-between">
          <span className="ml-1 pl-1">{options.find((o) => o.value === secondaryValue)?.label || secondaryValue}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs px-2 py-0 hover:bg-muted rounded-full flex items-center justify-center"
            onClick={(e) => {
              e.preventDefault();

              onChange(secondaryValue);
            }}
          >
            Switch
            <RefreshCw className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

const MultiSelectWrapper: React.FC<{
  value: string[] | undefined | null;
  secondaryValue?: string[] | null;
  onChange: (value: string[]) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  label: string | null;
  placeholder?: string | null;
  isRequired?: boolean;
  options: { value: string; label: string }[];
  doesConflict?: boolean;
  tabIndex?: number;
  error?: string;
  disabled?: boolean;
  showSelectAll?: boolean;
}> = ({
  value,
  secondaryValue,
  onChange,
  onFocus,
  onBlur,
  label,
  placeholder,
  isRequired,
  options,
  doesConflict,
  tabIndex,
  error,
  disabled,
  showSelectAll = true,
}) => {
  const selectedValues = value ?? [];
  const optionByValue = new Map(options.map((o) => [o.value, o] as const));
  const selectedOptions = selectedValues.map((v) => optionByValue.get(v) ?? { value: v, label: v });

  return (
    <div
      onFocus={onFocus}
      onBlur={(e) => {
        const relatedTarget = e.relatedTarget as HTMLElement | null;

        if (relatedTarget?.closest("[data-radix-popper-content-wrapper]")) {
          return;
        }

        if (!e.currentTarget.contains(relatedTarget)) {
          onBlur?.();
        }
      }}
    >
      {label && (
        <div className="mb-2 flex items-center justify-between">
          <Label className="text-muted-foreground">
            {label} {isRequired && <span className="text-red-500">*</span>}
          </Label>
        </div>
      )}
      <MultiSelect
        options={options}
        value={selectedOptions}
        onChange={(next) => onChange(next.map((o) => o.value))}
        placeholder={placeholder ?? "Select options..."}
        disabled={disabled}
        tabIndex={tabIndex}
        error={error}
        maxDisplay={10}
        chipClassName="font-normal text-foreground"
        showSelectAll={showSelectAll}
      />
      {doesConflict && secondaryValue && secondaryValue.length > 0 && (
        <div className="flex items-center mt-1 text-xs text-muted-foreground justify-between">
          <span className="ml-1 pl-1">{secondaryValue.map((v) => optionByValue.get(v)?.label ?? v).join(", ")}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs px-2 py-0 hover:bg-muted rounded-full flex items-center justify-center"
            onClick={(e) => {
              e.preventDefault();

              onChange(secondaryValue);
            }}
          >
            Switch
            <RefreshCw className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

// Define a generic field configuration type
type ListFieldConfig = {
  key: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  width?: string; // CSS width value
  type?: string; // input type
};

const ListInputWrapper: React.FC<{
  items: any[];
  secondaryItems?: any[];
  onChange: (items: any[]) => void;
  label: string | null;
  fields?: ListFieldConfig[];
  placeholder?: string;
  isRequired?: boolean;
  getDisplayValue?: (item: any, index: number) => React.ReactNode;
  doesConflict?: boolean;
  onFlagForInaccuracyUpdate?: (key: string, value: any) => void;
  fieldKey?: string;
  onFocus?: () => void;
}> = ({
  items = [],
  secondaryItems = [],
  onChange,
  label,
  getDisplayValue,
  fields = [{ key: "text", label: "Value", required: false }],
  doesConflict,
  onFlagForInaccuracyUpdate,
  fieldKey,
  onFocus,
}) => {
  const [showActionButtons, setShowActionButtons] = useState(false);

  // Capture initial items when component first mounts.
  const [initialItems] = useState(items);

  // Check if current items differ from initial items
  const hasChangedFromInitial = !isEqlArr(items, initialItems);

  // Set showActionButtons to true when items change from initial
  useEffect(() => {
    if (hasChangedFromInitial) {
      setShowActionButtons(true);
    }
  }, [hasChangedFromInitial]);

  const defaultGetDisplayValue = (item: any) => {
    if (typeof item === "object" && item?.text) {
      return item.text;
    }

    if ((fields?.length || 0) > 1) {
      return (
        fields
          ?.map((field) => item[field.key] || "")
          .filter(Boolean)
          .join(": ") || ""
      );
    }

    return String(item[fields?.[0]?.key] || "");
  };

  const displayValueFn = getDisplayValue || defaultGetDisplayValue;

  const displayedItems =
    doesConflict && secondaryItems
      ? Array.isArray(secondaryItems)
        ? secondaryItems
        : []
      : Array.isArray(items)
        ? items
        : [];

  const [newItem, setNewItem] = useState<Record<string, string>>(() => {
    return fields?.reduce((obj, field) => ({ ...obj, [field.key]: "" }), {}) || {};
  });

  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  const canAdd =
    fields?.every((field) => {
      return !field.required || (newItem[field.key] && newItem[field.key].trim() !== "");
    }) ?? true;

  const handleInputChange = (key: string, value: string) => {
    setNewItem((prev: Record<string, string>) => ({ ...prev, [key]: value }));

    if (fieldErrors[key]) {
      setFieldErrors((prev: Record<string, boolean>) => ({ ...prev, [key]: false }));
    }
  };

  const triggerChange = (newItems: any[]) => {
    setShowActionButtons(true);

    onChange(newItems);
  };

  const handleSave = () => {
    setShowActionButtons(false);

    // Call onFlagForInaccuracyUpdate if provided and we have a fieldKey
    onFlagForInaccuracyUpdate?.(fieldKey || "", items);
  };

  const addValue = () => {
    const errors: Record<string, boolean> = {};
    let hasError = false;

    fields?.forEach((field) => {
      if (field.required && (!newItem[field.key] || newItem[field.key].trim() === "")) {
        errors[field.key] = true;

        hasError = true;
      }
    });

    if (hasError) {
      setFieldErrors(errors);

      return;
    }

    const allEmpty = fields?.every((field) => !newItem[field.key] || newItem[field.key].trim() === "") ?? true;

    if (allEmpty) return;

    triggerChange([...displayedItems, { ...newItem }]);

    setNewItem(fields?.reduce((obj, field) => ({ ...obj, [field.key]: "" }), {}) || {});

    setFieldErrors({});
  };

  const removeValue = (index: number) => {
    triggerChange(displayedItems.filter((_, i) => i !== index));
  };

  const startEdit = (index: number) => {
    setEditIndex(index);
    const itemToEdit = displayedItems[index];

    const editFieldValues =
      fields?.reduce(
        (obj, field) => ({
          ...obj,
          [field.key]: itemToEdit?.[field.key] || "",
        }),
        {}
      ) || {};

    setEditValues(editFieldValues);
  };

  const handleEditChange = (key: string, value: string) => {
    setEditValues((prev: Record<string, string>) => ({ ...prev, [key]: value }));

    if (fieldErrors[key]) {
      setFieldErrors((prev: Record<string, boolean>) => ({ ...prev, [key]: false }));
    }
  };

  const saveEdit = () => {
    if (editIndex === null) return;

    const errors: Record<string, boolean> = {};
    let hasError = false;

    fields?.forEach((field) => {
      if (field.required && (!editValues[field.key] || editValues[field.key].trim() === "")) {
        errors[field.key] = true;

        hasError = true;
      }
    });

    if (hasError) {
      setFieldErrors(errors);

      return;
    }

    const newItems = [...displayedItems];

    newItems[editIndex] = { ...editValues };

    setEditIndex(null);

    setEditValues({});

    setFieldErrors({});

    triggerChange(newItems);
  };

  return (
    <div className="px-1" onFocus={onFocus} onClick={onFocus}>
      {label && (
        <div className="flex items-center justify-between">
          <Label className="text-muted-foreground">{label}</Label>
          {onFlagForInaccuracyUpdate && showActionButtons && hasChangedFromInitial && (
            <TooltipProvider>
              <Tooltip delayDuration={2000}>
                <TooltipTrigger asChild>
                  <Flag
                    className="h-3 w-3 text-orange-500 hover:text-orange-700"
                    onClick={(e) => {
                      e.stopPropagation();

                      handleSave();
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <span>Flag for AI extraction inaccuracy</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
      <Card
        className={`mt-2 p-4 w-full border-muted/80 rounded-sm shadow-sm ${doesConflict ? "bg-muted/10" : "bg-muted/5"}`}
        style={{ borderColor: doesConflict ? "orange" : "" }}
      >
        <div className="flex space-x-2 mb-4">
          <div className="flex-1 flex gap-2">
            {fields?.map((field) => (
              <div
                key={field.key}
                className="flex-grow"
                style={{
                  flexBasis: field.width || "auto",
                  width: field.width || "auto",
                }}
              >
                <Input
                  value={newItem[field.key] || ""}
                  className={`w-full ${fieldErrors[field.key] ? "border-red-500" : ""} ${doesConflict ? "bg-muted/10 text-muted-foreground" : "bg-input"}`}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  placeholder={field.placeholder || field.label || ""}
                  onKeyDown={(e) => e.key === "Enter" && addValue()}
                  type={field.type || "text"}
                />
                {fieldErrors[field.key] && <span className="text-xs text-red-500">Required</span>}
              </div>
            ))}
          </div>
          <Button
            className="ml-2 self-start"
            variant="outline"
            onClick={addValue}
            disabled={!canAdd}
            aria-disabled={!canAdd}
          >
            <Plus className={`w-4 h-4 ${!canAdd ? "text-gray-300" : ""}`} />
          </Button>
        </div>
        <ul className={`space-y-2 ${doesConflict ? "text-muted-foreground" : ""}`}>
          {displayedItems.map((item, index) => (
            <li key={index} className="flex space-x-2 items-center">
              {editIndex === index ? (
                <>
                  <div className="flex-1 flex gap-2">
                    {fields?.map((field) => (
                      <div
                        key={field.key}
                        className="flex-grow"
                        style={{
                          flexBasis: field.width || "auto",
                          width: field.width || "auto",
                        }}
                      >
                        <Input
                          value={editValues[field.key] || ""}
                          className={`w-full ${fieldErrors[field.key] ? "border-red-500" : ""}`}
                          onChange={(e) => handleEditChange(field.key, e.target.value)}
                          placeholder={field.placeholder || field.label || ""}
                          type={field.type || "text"}
                        />
                        {fieldErrors[field.key] && <span className="text-xs text-red-500">Required</span>}
                      </div>
                    ))}
                  </div>
                  <Button size="icon" onClick={saveEdit} className="shrink-0">
                    <Check className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <span className="flex-grow text-sm overflow-hidden text-ellipsis">{displayValueFn(item, index)}</span>
                  <div className="flex shrink-0 ml-auto">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(index)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => removeValue(index)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>

        {doesConflict && (
          <div className="flex items-center mt-3 text-xs text-muted-foreground justify-between">
            <span className="ml-1 pl-1">
              {doesConflict ? "Using matched data" : "Showing document data (matched data available)"}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs px-2 py-0 hover:bg-muted rounded-full flex items-center justify-center"
              onClick={(e) => {
                e.preventDefault();

                onChange(secondaryItems || []);
              }}
            >
              Switch
              <RefreshCw className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

const DateInputWrapper: React.FC<{
  value: Date | undefined | null;
  onChange: (date: Date | undefined) => void;
  label?: string | null;
  placeholder?: string;
  onSave?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  closeOnSelect?: boolean;
  isRequired?: boolean;
  error?: string;
  tabIndex?: number;
}> = ({
  value,
  onChange,
  label,
  placeholder,
  onSave,
  onFocus,
  onBlur,
  closeOnSelect = true,
  isRequired,
  error,
  tabIndex,
}) => {
  return (
    <div className="flex flex-col gap-2">
      {label && (
        <div className="flex items-center justify-between">
          <Label className="text-muted-foreground">
            {label} {isRequired && <span className="text-red-500">*</span>}
          </Label>
        </div>
      )}
      <div
        onFocus={onFocus}
        onBlur={() => {
          onSave?.();

          onBlur?.();
        }}
        tabIndex={tabIndex}
      >
        <DatePicker
          placeholder={placeholder}
          date={value ?? undefined}
          onDateChange={(d) => {
            onChange(d);
          }}
          closeOnSelect={closeOnSelect}
          className={clsx("w-full")}
        />
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      </div>
    </div>
  );
};

export {
  InputWrapper,
  CheckBoxWrapper,
  TextareaWrapper,
  SelectWrapper,
  SearchableSelectWrapper,
  MultiSelectWrapper,
  ListInputWrapper,
  DateInputWrapper,
};
export type { ListFieldConfig };
